import CreateBadgeModalButton from "@/admin/experiences/badges/CreateBadgeModalButton"
import { PathwaySettingsFormStore } from "@/admin/pathways/settings/PathwaySettingsForm"
import PathwaySettingsLabel from "@/admin/pathways/settings/PathwaySettingsLabel"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import { useLabel } from "@/core/context/LabelsContext"
import ROUTE_NAMES from "@/core/route/util/routeNames"
import ExperienceSettingsAdditionalDetails from "@/product/settings/ExperienceSettingsAdditionalDetails"
import ExperienceSettingsMetaDetailsFormFields from "@/product/settings/ExperienceSettingsMetaDetailsFormFields"
import ProductSettingsTags from "@/product/settings/ProductSettingsTags"
import ProductSlugInput from "@/product/settings/ProductSlugInput"
import { useAdminProductLabel } from "@/product/util/hook/useProductLabel"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import {
  DiscoDivider,
  DiscoFormControl,
  DiscoInput,
  DiscoLink,
  DiscoText,
} from "@disco-ui"
import DiscoImageFormField from "@disco-ui/file/DiscoImageFormField"
import DiscoFormCollapsibleSection, {
  DiscoFormCollapsibleSectionSkeleton,
} from "@disco-ui/form-collapsible-section/DiscoFormCollapsibleSection"
import { useMediaQuery, useTheme } from "@material-ui/core"
import { ASPECT_RATIOS } from "@utils/image/imageConstants"
import { TestIDProps } from "@utils/typeUtils"
import { observer } from "mobx-react-lite"
import { generatePath } from "react-router-dom"

type Props = TestIDProps & {
  form: PathwaySettingsFormStore
}

function PathwaySettingsCollapsibleDetails(props: Props) {
  const { testid = "PathwaySettingsDetails", form } = props
  const adminPathwayLabel = useAdminProductLabel("pathway")
  const membersLabel = useLabel("admin_member")

  const theme = useTheme()
  const isMdScreenUp = useMediaQuery(theme.breakpoints.up("md"))

  const pathwayDrawer = useGlobalDrawer("pathwaySettings")
  const sectionParam = pathwayDrawer.params.pathwaySettingsSection
  const isMetaDetailsOpen = sectionParam === "meta-details"

  return (
    <>
      <DiscoFormCollapsibleSection
        sectionId={"general-details"}
        icon={"edit"}
        title={"General Details"}
        description={`Customize the details of this ${adminPathwayLabel.singular} such as name, description, cover image.`}
        openByDefault={!isMetaDetailsOpen}
      >
        <DiscoFormCollapsibleSection.Group>
          <DiscoText marginBottom={2.5} variant={"body-md-600"}>
            {`${adminPathwayLabel.singular} Information`}
          </DiscoText>
          <DiscoFormControl
            sublabel={`Select a badge and a short name for this ${adminPathwayLabel.singular}.`}
            errorMessages={form.errorsByField.name || form.errorsByField.badgeId}
          >
            <DiscoInput
              data-testid={`${testid}.name`}
              value={form.state.name}
              onChange={(e) => (form.state.name = e.target.value)}
              fullWidth
              startAdornment={
                <CreateBadgeModalButton
                  badge={form.state.badge!}
                  testid={`${testid}.badge-picker`}
                />
              }
            />
          </DiscoFormControl>
          <DiscoFormControl
            sublabel={
              <DiscoText variant={"body-sm"} color={"text.secondary"}>
                {`This short description will be displayed on the ${adminPathwayLabel.singular} cards on the 'Explore' page. You can include a more robust description on your `}
                <DiscoLink
                  target={"_blank"}
                  to={generatePath(ROUTE_NAMES.PRODUCT.REGISTRATION.ROOT, {
                    productSlug: form.initialState.slug!,
                  })}
                  textVariant={"body-sm"}
                >
                  {"Registration Page"}
                </DiscoLink>
                {"."}
              </DiscoText>
            }
            errorMessages={form.errorsByField.description}
          >
            <DiscoInput
              data-testid={`${testid}.description`}
              value={form.state.description}
              onChange={(e) => (form.state.description = e.target.value)}
              fullWidth
              multiline
            />
          </DiscoFormControl>
          <DiscoFormControl
            variant={isMdScreenUp ? "two-column" : "one-column"}
            sublabel={`Add an image for use on ${adminPathwayLabel.singular} cards, registration, social share and more!`}
            errorMessages={form.errorsByField.cover}
          >
            <DiscoImageFormField
              value={form.state.cover}
              onChange={(result) => {
                form.state.cover = result?.url || null
                form.state.coverAssetId = result?.id
              }}
              showIcons
              disableRemove
              suggestedDimensions={{ width: 1200, height: 600 }}
              cropperProps={{ stencilProps: { aspectRatio: ASPECT_RATIOS.COVER_PHOTO } }}
              alterAspectRatio={false}
            />
          </DiscoFormControl>
        </DiscoFormCollapsibleSection.Group>
      </DiscoFormCollapsibleSection>
      <DiscoDivider marginTop={2.5} marginBottom={2.5} />
      <DiscoFormCollapsibleSection
        sectionId={"advanced"}
        icon={"widget-alt"}
        title={"Advanced"}
        description={"Customize tags, the URL and details on the registration page."}
        openByDefault={isMetaDetailsOpen}
      >
        <DiscoFormCollapsibleSection.Group>
          <ProductSettingsTags form={form} kind={"pathway"} />
        </DiscoFormCollapsibleSection.Group>
        <DiscoFormCollapsibleSection.Group>
          <DiscoFormControl
            label={
              <PathwaySettingsLabel
                title={"URL"}
                tooltipContent={`The base URL where your ${adminPathwayLabel.singular} lives. Visible to different ${membersLabel.plural} depending on your setup in the "Access" tab.`}
              />
            }
            sublabel={`The base URL for your ${adminPathwayLabel.singular}.`}
            errorMessages={form.errorsByField.slug}
          >
            <ProductSlugInput
              testid={testid}
              value={form.state.slug}
              onChange={(e) => (form.state.slug = e.target.value)}
            />
          </DiscoFormControl>
        </DiscoFormCollapsibleSection.Group>
        <DiscoFormCollapsibleSection.Group>
          <ExperienceSettingsMetaDetailsFormFields form={form} productType={"pathway"} />
        </DiscoFormCollapsibleSection.Group>
        {form.state.productId && (
          <DiscoFormCollapsibleSection.Group>
            <ExperienceSettingsAdditionalDetails
              productType={"pathway"}
              productId={form.state.productId}
            />
          </DiscoFormCollapsibleSection.Group>
        )}
      </DiscoFormCollapsibleSection>
    </>
  )
}

const useStyles = makeUseStyles({
  skeletonContainer: {
    display: "flex",
    flexDirection: "column",
  },
})

export function PathwaySettingsCollapsibleDetailsSkeleton() {
  const classes = useStyles()
  return (
    <div className={classes.skeletonContainer}>
      <DiscoFormCollapsibleSectionSkeleton />
      <DiscoDivider marginBottom={2.5} marginTop={2.5} />
      <DiscoFormCollapsibleSectionSkeleton />
      <DiscoDivider marginBottom={2.5} marginTop={2.5} />
      <DiscoFormCollapsibleSectionSkeleton />
    </div>
  )
}

export default observer(PathwaySettingsCollapsibleDetails)
